import React, {useState, useEffect} from 'react';
import './PinterestPage.css';

/*
 * 8 small photo polaroids around a large centered video polaroid.
 * Adjust top/left (%) and rotation (deg) to position each one manually.
 */
const POLAROIDS = [
    {id: 1, top: '8%', left: '10%', rotation: -3, delay: 0},
    {id: 2, top: '5%', left: '65%', rotation: 4, delay: 380},
    {id: 3, top: '12%', left: '50%', rotation: -2, delay: 760},
    {id: 4, top: '35%', left: '13%', rotation: 3, delay: 1140},
    {id: 5, top: '33%', left: '56%', rotation: -4, delay: 1520},
    {id: 6, top: '65%', left: '14%', rotation: 2, delay: 1900},
    {id: 7, top: '70%', left: '35%', rotation: -3, delay: 2280},
    {id: 8, top: '64%', left: '55%', rotation: 3.5, delay: 2660},
];

/* ─── Detailed hand-drawn pushpin (black & white) ─── */
function PinIcon({big}: { big?: boolean }) {
    const w = big ? 34 : 28;
    const h = big ? 46 : 38;
    return (
        <svg className="polaroid__pin" width={w} height={h} viewBox="0 0 28 38" fill="none">
            <ellipse cx="14" cy="9" rx="9" ry="7" fill="#fff" stroke="#222" strokeWidth="1.8"/>
            <ellipse cx="14" cy="8.5" rx="6" ry="4.5" fill="none" stroke="#222" strokeWidth="0.8" opacity="0.4"/>
            <path d="M10 6.5c1.5-1.5 5-1.8 7-0.5" stroke="#222" strokeWidth="0.7" fill="none" strokeLinecap="round"
                  opacity="0.5"/>
            <path d="M12.5 15.5 L13 18 L15 18 L15.5 15.5" stroke="#222" strokeWidth="1.2" fill="#ddd"/>
            <path d="M14 18 L14 36" stroke="#222" strokeWidth="1.5" strokeLinecap="round"/>
            <path d="M14 36 L13.5 34" stroke="#222" strokeWidth="0.8" strokeLinecap="round" opacity="0.4"/>
            <circle cx="14" cy="36.5" r="1" fill="#222" opacity="0.25"/>
        </svg>
    );
}

/* ─── Sketch border SVG (reused) ─── */
function SketchBorderOuter() {
    return (
        <svg className="polaroid__border" viewBox="0 0 200 240" preserveAspectRatio="none">
            <path
                d="M10,6 C50,4 150,8 190,5 C194,8 197,12 198,16 C199,60 197,180 199,224 C197,228 194,232 190,234 C140,236 60,233 10,235 C6,233 3,228 2,224 C1,160 3,40 1,16 C3,12 6,8 10,6 Z"
                fill="none" stroke="#222" strokeWidth="2"
            />
        </svg>
    );
}

function SketchBorderInner() {
    return (
        <svg className="polaroid__photo-wrapper__border" viewBox="0 0 200 200" preserveAspectRatio="none">
            <path
                d="M8,5 C50,3 150,7 192,4 C195,7 198,11 199,15 C200,50 198,160 199,185 C198,189 195,193 192,195 C140,197 60,194 8,196 C5,194 2,189 1,185 C0,130 2,40 1,15 C2,11 5,7 8,5 Z"
                fill="none" stroke="#222" strokeWidth="2"
            />
        </svg>
    );
}

/* ─── Small photo Polaroid ─── */
function Polaroid({id, top, left, rotation, delay}: {
    id: number; top: string; left: string; rotation: number; delay: number;
}) {
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        const timer = setTimeout(() => setVisible(true), delay);
        return () => clearTimeout(timer);
    }, [delay]);

    if (!visible) return null;

    return (
        <div
            className="polaroid polaroid--appear"
            style={{top, left, transform: `rotate(${rotation}deg)`}}
        >
            <PinIcon/>
            <div className="polaroid__frame">
                <div className="polaroid__inner">
                    <SketchBorderOuter/>
                    <div className="polaroid__photo-wrapper">
                        <SketchBorderInner/>
                        <div className="polaroid__photo">
                            <img
                                src={`/danse${id}.jpg`}
                                alt={`Photo ${id}`}
                                className="polaroid__img"
                                onError={(e) => {
                                    (e.target as HTMLImageElement).style.display = 'none';
                                }}
                            />
                            <span className="polaroid__placeholder">Photo {id}</span>
                        </div>
                    </div>
                    <div className="polaroid__caption">&nbsp;</div>
                </div>
            </div>
        </div>
    );
}

/* ─── Large centered video Polaroid ─── */
function VideoPolaroid() {
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        /* Appear after all 8 small polaroids (8 × 380ms ≈ 3040ms + buffer) */
        const timer = setTimeout(() => setVisible(true), 3400);
        return () => clearTimeout(timer);
    }, []);

    if (!visible) return null;

    return (
        <div className="polaroid polaroid--video polaroid--appear">
            <PinIcon big/>
            <div className="polaroid__frame">
                <div className="polaroid__inner">
                    {/* Outer sketch border (wider viewBox for landscape) */}
                    <svg className="polaroid__border" viewBox="0 0 300 240" preserveAspectRatio="none">
                        <path
                            d="M12,6 C80,3 220,8 288,5 C293,8 296,12 298,16 C299,60 297,180 299,224 C297,228 293,232 288,234 C200,236 100,233 12,235 C7,233 4,228 2,224 C1,160 3,40 1,16 C3,12 7,8 12,6 Z"
                            fill="none" stroke="#222" strokeWidth="2"
                        />
                    </svg>

                    <div className="polaroid__photo-wrapper">
                        <svg className="polaroid__photo-wrapper__border" viewBox="0 0 300 200"
                             preserveAspectRatio="none">
                            <path
                                d="M10,5 C80,3 220,7 290,4 C294,7 297,11 298,15 C299,50 297,160 299,185 C297,189 294,193 290,195 C200,197 100,194 10,196 C6,194 3,189 2,185 C1,130 3,40 1,15 C3,11 6,7 10,5 Z"
                                fill="none" stroke="#222" strokeWidth="2"
                            />
                        </svg>
                        <div className="polaroid__photo polaroid__photo--video">
                            <video
                                className="polaroid__video"
                                src="/danse.mp4"
                                autoPlay
                                loop
                                muted
                                playsInline
                            />
                            <span className="polaroid__placeholder">Video</span>
                        </div>
                    </div>
                    <div className="polaroid__caption polaroid__caption--text">
                        <span>Dress code de fête : venez comme vous êtes,</span>
                        <span>en <strong>costard</strong> ou en survet</span>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default function PinterestPage() {
    return (
        <>
            <VideoPolaroid/>
            {POLAROIDS.map(p => (
                <Polaroid key={p.id} {...p}/>
            ))}
        </>
    );
}
